import React, { useEffect, useState } from "react";
import api, { downloadAuthenticatedPdf } from "../../lib/api";
import { getUser, formatMoney } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { FileText, Plus, Send, Download, Trash2, ExternalLink, Eye } from "lucide-react";

const STATUS_LABEL = {
  draft: { label: "Rascunho", cls: "bg-slate-100 text-slate-700" },
  ready: { label: "Gerado", cls: "bg-sky-100 text-sky-700" },
  awaiting_guest: { label: "Aguardando hóspede", cls: "bg-amber-100 text-amber-800" },
  accepted: { label: "Aceito", cls: "bg-emerald-100 text-emerald-700" },
  signed: { label: "Assinado", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelado", cls: "bg-red-100 text-red-700" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`text-[11px] px-2 py-0.5 rounded ${s.cls}`} data-testid={`contract-status-${status}`}>{s.label}</span>;
}

export default function AdminContracts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/contracts");
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar contratos");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl" data-testid="admin-contracts-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Documentos</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Contratos</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Contratos de locação por temporada gerados a partir de reservas confirmadas.
            Aceite eletrônico registrado com evidência forense (Lei 14.063/2020).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => (window.location.href = "/admin/contratos/modelo")} data-testid="edit-template-btn">
            Editar modelo padrão
          </Button>
          <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={() => setShowNew(true)} data-testid="new-contract-btn">
            <Plus className="w-4 h-4 mr-1" /> Novo contrato
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
          <FileText className="w-8 h-8 text-slate-400 mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Nenhum contrato criado ainda.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Hóspede</th>
                <th className="px-4 py-3 text-left">Casa</th>
                <th className="px-4 py-3 text-left">Reserva</th>
                <th className="px-4 py-3 text-left">Total</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`contract-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#22302E]">{c.guest_name || "—"}</div>
                    <div className="text-xs text-slate-500">{c.guest_email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{c.house_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{c.reservation?.code || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{c.reservation?.total ? formatMoney(c.reservation.total) : "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`open-contract-${c.id}`}>Abrir</Button>
                    <Button
                      size="sm" variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm("Mover este contrato para a Lixeira?")) return;
                        try {
                          await api.delete(`/admin/contracts/${c.id}`);
                          toast.success("Contrato movido para Lixeira");
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
                      }}
                      data-testid={`delete-contract-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewContractDialog onClose={() => { setShowNew(false); load(); }} />}
      {editing && <ContractDetailDialog contract={editing} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function NewContractDialog({ onClose }) {
  const [reservations, setReservations] = useState([]);
  const [resId, setResId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/reservations").then((r) => {
      // Only confirmed reservations without an active contract are candidates
      setReservations((r.data || []).filter((x) => ["paid", "confirmed"].includes(x.status)));
    });
  }, []);

  async function submit() {
    if (!resId) { toast.error("Selecione uma reserva"); return; }
    setSaving(true);
    try {
      await api.post("/admin/contracts", { reservation_id: resId });
      toast.success("Contrato criado como rascunho");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar contrato");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo contrato</DialogTitle>
          <DialogDescription>Selecione a reserva base. O contrato será criado em rascunho — você preencherá as condições de pagamento a seguir.</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Reserva</Label>
          <Select value={resId} onValueChange={setResId}>
            <SelectTrigger data-testid="new-contract-reservation"><SelectValue placeholder="Selecione uma reserva paga/confirmada" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {reservations.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.code} — {r.guest?.name} — {r.check_in} → {r.check_out} — {formatMoney(r.total || 0)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={submit} disabled={saving} data-testid="create-contract-submit">
            {saving ? "Criando…" : "Criar rascunho"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContractDetailDialog({ contract: initial, onClose }) {
  const [c, setC] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const readonly = ["accepted", "signed", "cancelled"].includes(c.status);
  const total = c.reservation?.total || 0;

  const updateProfile = (k, v) => setC({ ...c, guest_profile: { ...(c.guest_profile || {}), [k]: v } });
  const updateTerms = (k, v) => setC({ ...c, payment_terms: { ...(c.payment_terms || {}), [k]: v } });

  function updateInstallment(i, k, v) {
    const list = [...(c.payment_terms?.installments || [])];
    list[i] = { ...list[i], [k]: k === "amount" ? Number(v) : v };
    updateTerms("installments", list);
  }
  function addInstallment() {
    const list = [...(c.payment_terms?.installments || [])];
    if (list.length >= 5) { toast.error("Máximo 5 parcelas"); return; }
    list.push({ seq: list.length + 1, amount: 0, due_date: "" });
    updateTerms("installments", list);
  }
  function removeInstallment(i) {
    const list = [...(c.payment_terms?.installments || [])].filter((_, idx) => idx !== i)
      .map((x, idx) => ({ ...x, seq: idx + 1 }));
    updateTerms("installments", list);
  }

  async function save() {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/contracts/${c.id}`, {
        guest_name: c.guest_name,
        guest_cpf: c.guest_cpf,
        guest_profile: c.guest_profile,
        payment_terms: c.payment_terms,
        witnesses: c.witnesses || [],
      });
      setC({ ...c, ...r.data });
      toast.success("Alterações salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  }

  async function generatePdf() {
    setBusy(true);
    try {
      const r = await api.post(`/admin/contracts/${c.id}/generate-pdf`);
      setC({ ...c, ...r.data });
      toast.success("PDF gerado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar PDF"); }
    finally { setBusy(false); }
  }

  async function sendLink() {
    if (!confirm(`Enviar link de preenchimento por e-mail para ${c.guest_email}? O link expira em 7 dias.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/contracts/${c.id}/send-fill-link`);
      toast.success("Link enviado ao hóspede");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar link"); }
    finally { setBusy(false); }
  }

  const p = c.guest_profile || {};
  const pt = c.payment_terms || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Contrato — {c.guest_name || "sem hóspede"}</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <StatusBadge status={c.status} />
            <span>Reserva {c.reservation?.code || c.reservation_id?.slice(0, 8)} — Total {formatMoney(total)}</span>
          </DialogDescription>
        </DialogHeader>

        {readonly && (
          <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
            Contrato {c.status === "accepted" ? "aceito" : c.status} — edição bloqueada. Você ainda pode baixar o PDF final.
            {c.acceptance && (
              <div className="mt-2 text-xs">
                Aceite por <b>{c.acceptance.accepted_name}</b> (CPF {c.acceptance.accepted_cpf}) em {c.acceptance.accepted_at?.slice(0, 19).replace("T", " ")} · IP {c.acceptance.accepted_ip}
                <div>Hash SHA-256: <code>{c.acceptance.pdf_sha256?.slice(0, 32)}…</code></div>
              </div>
            )}
          </div>
        )}

        <section className="mt-4">
          <h3 className="text-sm font-semibold text-[#0AA89C] mb-3">Dados do locatário</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome completo</Label><Input disabled={readonly} value={c.guest_name || ""} onChange={(e) => setC({ ...c, guest_name: e.target.value })} data-testid="ct-guest-name" /></div>
            <div><Label>CPF</Label><Input disabled={readonly} value={c.guest_cpf || ""} onChange={(e) => setC({ ...c, guest_cpf: e.target.value })} data-testid="ct-guest-cpf" /></div>
            <div><Label>Nacionalidade</Label><Input disabled={readonly} value={p.nationality || ""} onChange={(e) => updateProfile("nationality", e.target.value)} /></div>
            <div><Label>Naturalidade</Label><Input disabled={readonly} value={p.naturalidade || ""} onChange={(e) => updateProfile("naturalidade", e.target.value)} /></div>
            <div><Label>Data de nascimento</Label><Input type="date" disabled={readonly} value={p.date_of_birth || ""} onChange={(e) => updateProfile("date_of_birth", e.target.value)} /></div>
            <div><Label>Estado civil</Label><Input disabled={readonly} value={p.civil_state || ""} onChange={(e) => updateProfile("civil_state", e.target.value)} /></div>
            <div><Label>Profissão</Label><Input disabled={readonly} value={p.profession || ""} onChange={(e) => updateProfile("profession", e.target.value)} /></div>
            <div>
              <Label>Tipo de documento</Label>
              <Select value={p.doc_type || ""} onValueChange={(v) => updateProfile("doc_type", v)} disabled={readonly}>
                <SelectTrigger data-testid="ct-doc-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="rg">RG</SelectItem>
                  <SelectItem value="passport">Passaporte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Número do documento</Label><Input disabled={readonly} value={p.doc_number || ""} onChange={(e) => updateProfile("doc_number", e.target.value)} /></div>
            <div><Label>Órgão emissor</Label><Input disabled={readonly} value={p.doc_emissor || ""} onChange={(e) => updateProfile("doc_emissor", e.target.value)} /></div>
            <div><Label>Data de emissão</Label><Input type="date" disabled={readonly} value={p.doc_emit_date || ""} onChange={(e) => updateProfile("doc_emit_date", e.target.value)} /></div>
            <div><Label>Telefone alternativo</Label><Input disabled={readonly} value={p.phone_alt || ""} onChange={(e) => updateProfile("phone_alt", e.target.value)} /></div>
          </div>
        </section>

        <section className="mt-6">
          <h3 className="text-sm font-semibold text-[#0AA89C] mb-3">Endereço de cobrança</h3>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-4"><Label>Logradouro</Label><Input disabled={readonly} value={p.address_street || ""} onChange={(e) => updateProfile("address_street", e.target.value)} /></div>
            <div className="col-span-2"><Label>Número</Label><Input disabled={readonly} value={p.address_number || ""} onChange={(e) => updateProfile("address_number", e.target.value)} /></div>
            <div className="col-span-3"><Label>Complemento</Label><Input disabled={readonly} value={p.address_complement || ""} onChange={(e) => updateProfile("address_complement", e.target.value)} /></div>
            <div className="col-span-3"><Label>Bairro</Label><Input disabled={readonly} value={p.address_neighborhood || ""} onChange={(e) => updateProfile("address_neighborhood", e.target.value)} /></div>
            <div className="col-span-3"><Label>Cidade</Label><Input disabled={readonly} value={p.address_city || ""} onChange={(e) => updateProfile("address_city", e.target.value)} /></div>
            <div className="col-span-1"><Label>UF</Label><Input disabled={readonly} maxLength={2} value={p.address_state || ""} onChange={(e) => updateProfile("address_state", e.target.value.toUpperCase())} /></div>
            <div className="col-span-2"><Label>CEP</Label><Input disabled={readonly} value={p.address_postal_code || ""} onChange={(e) => updateProfile("address_postal_code", e.target.value)} /></div>
          </div>
        </section>

        <section className="mt-6">
          <h3 className="text-sm font-semibold text-[#0AA89C] mb-3">Cláusula 10ª — Condições de Pagamento e Cancelamento</h3>
          <div className="text-xs text-slate-500 mb-3">
            Multa por cancelamento: <b>30% fixa</b> (não editável). Sinal e parcelas: preencha conforme a negociação desta reserva específica.
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label>Percentual de sinal (%)</Label>
              <Input disabled={readonly} type="number" placeholder="ex. 50" min={0} max={100} value={pt.signal_pct ?? ""} onChange={(e) => updateTerms("signal_pct", e.target.value === "" ? null : Number(e.target.value))} data-testid="ct-signal-pct" />
            </div>
            <div>
              <Label>Forma do saldo remanescente</Label>
              <Select value={pt.remainder_form || ""} onValueChange={(v) => updateTerms("remainder_form", v)} disabled={readonly}>
                <SelectTrigger data-testid="ct-remainder-form"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="lump_sum">À vista</SelectItem>
                  <SelectItem value="installments">Parcelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500">
              <div>Multa cancelamento</div>
              <div className="mt-1 text-sm font-medium text-[#22302E]">30% (fixa)</div>
            </div>
          </div>

          {pt.remainder_form === "installments" && (
            <div className="mt-4 border border-slate-200 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500">Parcelas (até 5)</div>
                {!readonly && <Button size="sm" variant="outline" onClick={addInstallment} data-testid="ct-add-installment">+ Adicionar</Button>}
              </div>
              {(pt.installments || []).map((inst, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-1 text-sm pb-2">#{inst.seq}</div>
                  <div className="col-span-4">
                    <Label>Valor</Label>
                    <Input disabled={readonly} type="number" step="0.01" value={inst.amount || ""} onChange={(e) => updateInstallment(i, "amount", e.target.value)} data-testid={`ct-inst-amount-${i}`} />
                  </div>
                  <div className="col-span-4">
                    <Label>Vencimento</Label>
                    <Input disabled={readonly} type="date" value={inst.due_date || ""} onChange={(e) => updateInstallment(i, "due_date", e.target.value)} data-testid={`ct-inst-date-${i}`} />
                  </div>
                  <div className="col-span-2">
                    <Label>Status</Label>
                    <div className="text-xs pt-2">{inst.status === "paid" ? "Pago" : "Pendente"}</div>
                  </div>
                  {!readonly && (
                    <div className="col-span-1">
                      <button onClick={() => removeInstallment(i)} className="text-red-500 hover:text-red-700 pb-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-6">
          <h3 className="text-sm font-semibold text-[#0AA89C] mb-3">Testemunhas (opcional)</h3>
          <div className="text-xs text-slate-500 mb-2">Não obrigatórias — o contrato é válido apenas com Locador e Locatário.</div>
          {[0, 1].map((i) => (
            <div key={i} className="grid grid-cols-2 gap-3 mb-2">
              <div><Label>Testemunha {i + 1} — Nome</Label><Input disabled={readonly} value={(c.witnesses?.[i]?.name) || ""} onChange={(e) => {
                const w = [...(c.witnesses || [])]; while (w.length <= i) w.push({}); w[i] = { ...w[i], name: e.target.value };
                setC({ ...c, witnesses: w });
              }} /></div>
              <div><Label>CPF</Label><Input disabled={readonly} value={(c.witnesses?.[i]?.cpf) || ""} onChange={(e) => {
                const w = [...(c.witnesses || [])]; while (w.length <= i) w.push({}); w[i] = { ...w[i], cpf: e.target.value };
                setC({ ...c, witnesses: w });
              }} /></div>
            </div>
          ))}
        </section>

        <div className="sticky bottom-0 -mx-6 -mb-6 mt-8 bg-white border-t border-slate-200 p-4 flex justify-between items-center">
          <div className="flex gap-2">
            {c.pdf_path && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadAuthenticatedPdf(`/admin/contracts/${c.id}/pdf`, "inline", `contrato-${(c.reservation?.code || c.id.slice(0, 8))}.pdf`);
                    } catch (e) { toast.error(e.message || "Erro ao abrir PDF"); }
                  }}
                  className="text-sm text-[#0AA89C] hover:text-[#066B63] inline-flex items-center gap-1"
                  data-testid="ct-view-pdf"
                >
                  <Eye className="w-4 h-4" /> Visualizar PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadAuthenticatedPdf(`/admin/contracts/${c.id}/pdf`, "download", `contrato-${(c.reservation?.code || c.id.slice(0, 8))}.pdf`);
                    } catch (e) { toast.error(e.message || "Erro ao baixar PDF"); }
                  }}
                  className="text-sm text-[#0AA89C] hover:text-[#066B63] inline-flex items-center gap-1"
                  data-testid="ct-download-pdf"
                >
                  <Download className="w-4 h-4" /> Baixar PDF
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            {!readonly && (
              <>
                <Button variant="outline" onClick={save} disabled={saving} data-testid="ct-save">{saving ? "Salvando…" : "Salvar"}</Button>
                <Button variant="outline" onClick={generatePdf} disabled={busy} data-testid="ct-generate-pdf"><Eye className="w-4 h-4 mr-1" /> Gerar PDF</Button>
                <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={sendLink} disabled={busy} data-testid="ct-send-link"><Send className="w-4 h-4 mr-1" /> Enviar ao hóspede</Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
